import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Card, Chip, EmptyState, PageHeader, PageLoading, Skeleton, Switch } from '@koyomi/ui';
import { useCalendar } from '../../lib/calendar-api.ts';
import { useBusiness, useMembers, useSave } from '../../lib/queries.ts';
import { api } from '../../lib/api.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import { endOfDayIso, fmtDay, shiftDay, startOfDayIso, timeOf, todayIn } from '../../lib/time.ts';

/**
 * "Meu resumo do dia" (usuário padrão): o mesmo conteúdo do resumo diário enviado no WhatsApp
 * (agendamentos do membro no dia, almoço) montado a partir da agenda, mais as preferências de aviso.
 */
export function MySummaryPage() {
  const { t, i18n } = useTranslation(['calendar', 'config', 'nav', 'common']);
  const business = useBusiness();
  const members = useMembers();
  const me = useAuthStore((s) => s.businesses.find((b) => b.id === s.businessId));
  const user = useAuthStore((s) => s.user);
  const tz = business.data?.timezone ?? 'UTC';
  const [dayState, setDay] = useState<string | null>(null);
  const day = dayState ?? todayIn(tz);
  const memberId = me?.memberId ?? '';
  const cal = useCalendar(startOfDayIso(day, tz), endOfDayIso(day, tz), memberId ? [memberId] : []);
  const savePrefs = useSave((v: { dailySummaryEnabled?: boolean; whatsappNoticesEnabled?: boolean }) => api(`/business/members/${memberId}`, { method: 'PATCH', body: v }), ['members']);
  if (business.isLoading || members.isLoading) return <><PageHeader title={t('nav:config.mySummary')} /><PageLoading cards={2} rows={4} /></>;
  if (!business.data) return null;
  const member = (members.data ?? []).find((m) => m.id === memberId);
  const appts = (cal.data?.appointments ?? []).filter((a) => a.status === 'CONFIRMED').sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const dow = String(new Date(day + 'T12:00:00Z').getUTCDay());
  const lunch = business.data.hours[dow as keyof typeof business.data.hours]?.lunch ?? null;
  const first = (user?.name ?? '').split(' ')[0] ?? '';
  const isToday = day === todayIn(tz);
  const dateLabel = fmtDay(day, i18n.language, { weekday: 'long', day: '2-digit', month: '2-digit' });
  return (
    <>
      <PageHeader title={t('nav:config.mySummary')} subtitle={t('calendar:mySummary.greeting', { name: first, date: dateLabel })}
        actions={<><Button size="sm" variant="ghost" onClick={() => setDay(shiftDay(day, -1))}>‹</Button>{!isToday && <Button size="sm" variant="ghost" onClick={() => setDay(todayIn(tz))}>{t('calendar:today')}</Button>}<Button size="sm" variant="ghost" onClick={() => setDay(shiftDay(day, 1))}>›</Button></>} />
      <div className="k-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))' }}>
        <Card title={t('calendar:mySummary.appointments')} subtitle={cal.data ? t('calendar:mySummary.count', { count: appts.length }) + (lunch ? ` · ${t('config:hours.lunch')} ${lunch.start}–${lunch.end}` : '') : undefined}>
          {cal.isLoading ? <Skeleton lines={4} /> : appts.length === 0 ? <EmptyState title={t('calendar:noAppointments')} /> : (
            <div className="k-kv">
              {appts.map((a) => (
                <div key={a.id} className="k-kv__row" style={{ minHeight: 44 }}>
                  <span className="k-row" style={{ gap: 10, flexWrap: 'wrap' }}>
                    <strong style={{ minWidth: 48 }}>{timeOf(a.startsAt, tz)}</strong>
                    <span>{a.snapshot.segments.map((s) => s.serviceName).join(' + ')}{a.modality === 'HOME' ? ` · ${t('calendar:mySummary.home', { region: a.snapshot.regionName ?? '' })}` : ''}</span>
                    <span className="k-muted">{a.snapshot.clientName}</span>
                  </span>
                  <span className="k-row" style={{ gap: 6 }}>
                    {a.confirmationStatus === 'CONFIRMED' && <Chip tone="accent">{t('calendar:status.confirmed')}</Chip>}
                    <Link to={`/clientes/${a.clientId}`} style={{ fontSize: 13 }}>{t('calendar:mySummary.openClient')}</Link>
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="k-muted" style={{ fontSize: 13, marginBottom: 0 }}><Link to="/agenda">{t('nav:main.myAgenda')}</Link></p>
        </Card>
        <Card title={t('calendar:mySummary.prefs')} subtitle={t('calendar:mySummary.prefsHint', { time: business.data.settings.summaries.dailySummaryAt })}>
          {member ? (
            <div className="k-stack" style={{ gap: 6 }}>
              <Switch label={t('config:team.summary')} description={t('config:team.summaryHint')} disabled={savePrefs.isPending} checked={member.dailySummaryEnabled} onChange={(v) => savePrefs.mutate({ dailySummaryEnabled: v })} />
              <Switch label={t('config:team.notices')} description={t('config:team.noticesHint')} disabled={savePrefs.isPending} checked={member.whatsappNoticesEnabled} onChange={(v) => savePrefs.mutate({ whatsappNoticesEnabled: v })} />
              {!user?.phone && <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>{t('calendar:mySummary.noPhone')} <Link to="/perfil">{t('nav:profile')}</Link></p>}
            </div>
          ) : <Skeleton lines={2} />}
        </Card>
      </div>
    </>
  );
}
